// Constants
const UPDATE_COUNTDOWN_SECONDS = parseInt(process.env.VUE_APP_UPDATE_COUNTDOWN_SECONDS) || 600

export default {
  data() {
    return {
      pongCounter: 0,
      updateDetected: false,
      refreshCountdown: UPDATE_COUNTDOWN_SECONDS,
      countdownInterval: null,
      reloadTimeoutId: null,
      isCheckingSW: false,
      controllerChangeHandlerAttached: false,
      waitingWorker: null,
      swUpdateChannel: null,
      registration: null,
      visibilityHandler: null,
      updateFoundHandler: null,
      storageHandler: null,
      lastSaveTime: 0,
    }
  },

  async created() {
    this.setupBroadcastChannel()
    this.setupStorageListener()
    this.setupVisibilityListener()

    const restored = await this.restoreFromStorage()

    if (restored === 'refreshing') {
      return
    }

    await this.setupServiceWorkerListener()

    if (!restored) {
      this.checkServiceWorkerUpdate()
    }
  },

  beforeDestroy() {
    if (this.swUpdateChannel) {
      this.swUpdateChannel.close()
    }

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }

    if (this.registration && this.updateFoundHandler) {
      this.registration.removeEventListener('updatefound', this.updateFoundHandler)
    }

    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler)
    }

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }

    // Note: Don't remove controllerchange listener - need it to complete reload
  },

  methods: {
    async restoreFromStorage() {
      try {
        const saved = localStorage.getItem('vue-update-state')
        if (!saved) {
          return false
        }

        const savedState = JSON.parse(saved)
        const elapsed = Math.floor((Date.now() - savedState.timestamp) / 1000)
        const remainingTime = savedState.refreshCountdown - elapsed

        const registration = await navigator.serviceWorker.getRegistration()

        if (!registration || !registration.waiting) {
          localStorage.removeItem('vue-update-state')
          return false
        }

        this.registration = registration
        this.waitingWorker = registration.waiting

        if (remainingTime > 0 && savedState.updateDetected) {
          this.updateDetected = true
          this.refreshCountdown = remainingTime
          this.startCountdownTimer(remainingTime)
          return true
        } else if (remainingTime <= 0 && savedState.updateDetected) {
          this.refreshApp()
          return 'refreshing'
        }

        localStorage.removeItem('vue-update-state')
        return false
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error restoring state:', error)
        this.clearSavedState()
        return false
      }
    },

    saveToStorage() {
      try {
        const now = Date.now()
        if (now - this.lastSaveTime < 1000) {
          return
        }
        this.lastSaveTime = now

        const data = {
          updateDetected: this.updateDetected,
          refreshCountdown: this.refreshCountdown,
          timestamp: now,
        }
        localStorage.setItem('vue-update-state', JSON.stringify(data))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save to localStorage:', error)
      }
    },

    clearSavedState() {
      try {
        localStorage.removeItem('vue-update-state')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error clearing localStorage:', error)
      }
    },

    setupVisibilityListener() {
      this.visibilityHandler = () => {
        if (document.hidden && this.updateDetected) {
          this.saveToStorage()
        }
      }
      document.addEventListener('visibilitychange', this.visibilityHandler)
    },

    setupStorageListener() {
      this.storageHandler = this.handleStorageChange.bind(this)
      window.addEventListener('storage', this.storageHandler)
    },

    handleStorageChange(e) {
      if (e.key !== 'vue-update-state') return

      if (e.newValue === null) {
        window.location.reload()
      } else {
        try {
          const state = JSON.parse(e.newValue)
          this.refreshCountdown = state.refreshCountdown
          this.updateDetected = state.updateDetected
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error parsing storage change:', error)
        }
      }
    },

    async checkServiceWorkerUpdate() {
      try {
        const registration = this.registration || (await navigator.serviceWorker.getRegistration())

        this.registration = registration
        await registration.update()

        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking for updates:', error)
        return false
      }
    },

    setupBroadcastChannel() {
      if (typeof BroadcastChannel === 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('BroadcastChannel not supported')
        return
      }

      try {
        this.swUpdateChannel = new BroadcastChannel('sw-update-channel')

        this.swUpdateChannel.onmessage = (event) => {
          try {
            const { type, countdown } = event.data

            switch (type) {
              case 'SW_UPDATE_AVAILABLE':
                if (!this.updateDetected && countdown !== undefined) {
                  this.syncFromOtherTab(countdown)
                }
                break

              case 'SW_ACTIVATING':
                this.closeChannelAndReload()
                break

              case 'COUNTDOWN_SYNC':
                if (this.updateDetected && countdown !== undefined) {
                  this.refreshCountdown = countdown
                }
                break
            }
          } catch (error) {
            // Silently handle message errors
          }
        }
        this.swUpdateChannel.onerror = () => {
          // Silently ignore channel errors
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error setting up BroadcastChannel:', error)
      }
    },

    async setupServiceWorkerListener() {
      if (!('serviceWorker' in navigator)) {
        // eslint-disable-next-line no-console
        console.warn('Service Worker not supported')
        return
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration()

        this.updateFoundHandler = () => {
          const newWorker = registration.installing

          if (!newWorker) return

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // eslint-disable-next-line no-console
                console.log('✅ New SW installed and ready!')
                this.activateNewServiceWorker(newWorker)
              }
            }
          })
        }

        registration.addEventListener('updatefound', this.updateFoundHandler)

        // Check if there's already a waiting SW (page refresh during update)
        if (registration.waiting && navigator.serviceWorker.controller) {
          // Use nextTick to ensure component is fully initialized
          this.$nextTick(() => {
            if (!this.updateDetected) {
              this.activateNewServiceWorker(registration.waiting)
            } else {
              this.waitingWorker = registration.waiting
            }
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error setting up SW listener:', error)
      }
    },

    syncFromOtherTab(targetCountdown) {
      this.refreshCountdown = targetCountdown
      this.updateDetected = true
      this.saveToStorage()

      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }

      this.startCountdownTimer(targetCountdown)
    },

    startCountdownTimer(seconds) {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }

      // Set the initial countdown value
      this.refreshCountdown = seconds

      this.countdownInterval = setInterval(() => {
        if (this.refreshCountdown > 0) {
          this.refreshCountdown--

          if (this.refreshCountdown % 5 === 0) {
            this.saveToStorage()

            if (this.swUpdateChannel) {
              this.swUpdateChannel.postMessage({
                type: 'COUNTDOWN_SYNC',
                countdown: this.refreshCountdown,
              })
            }
          }
        } else {
          clearInterval(this.countdownInterval)
          this.refreshApp()
        }
      }, 1000)
    },

    refreshApp() {
      // Clear state
      this.updateDetected = false
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }

      // Clear localStorage
      this.clearSavedState()

      // Notify other tabs before reloading
      if (this.swUpdateChannel) {
        try {
          this.swUpdateChannel.postMessage({
            type: 'SW_ACTIVATING',
          })
        } catch (error) {
          // Silently ignore if channel is already closed
        }
      }

      // Activate and reload
      if (!this.waitingWorker) {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration && registration.waiting) {
            this.waitingWorker = registration.waiting
            this.activateAndReload()
          } else {
            // eslint-disable-next-line no-console
            console.error('❌ No waiting worker found, force reload anyway!')
            this.closeChannelAndReload()
          }
        })
      } else {
        this.activateAndReload()
      }
    },

    // Close channel properly and reload after delay
    closeChannelAndReload() {
      if (this.swUpdateChannel) {
        try {
          this.swUpdateChannel.close()
          this.swUpdateChannel = null
        } catch (error) {
          // Ignore errors
        }
      }

      // Delay to ensure messages are sent before reload
      setTimeout(() => {
        window.location.reload()
      }, 150)
    },

    // Activate new service worker and reload
    activateAndReload() {
      if (!this.controllerChangeHandlerAttached) {
        this.controllerChangeHandlerAttached = true

        navigator.serviceWorker.addEventListener('controllerchange', () => {
          // eslint-disable-next-line no-console
          console.log('New Service Worker activated!')
          // Clear fallback timeout to prevent double reload
          if (this.reloadTimeoutId) {
            clearTimeout(this.reloadTimeoutId)
            this.reloadTimeoutId = null
          }
          this.closeChannelAndReload()
        })
      }

      try {
        this.waitingWorker.postMessage({ type: 'SKIP_WAITING' })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error sending signal:', error)
        this.closeChannelAndReload()
        return
      }

      // Fallback timeout in case controllerchange doesn't fire
      this.reloadTimeoutId = setTimeout(() => {
        this.closeChannelAndReload()
      }, 5000)
    },

    activateNewServiceWorker(worker) {
      if (!worker) {
        return
      }

      if (this.updateDetected) {
        if (this.waitingWorker !== worker) {
          this.waitingWorker = worker
        }
        return
      }

      this.waitingWorker = worker
      this.updateDetected = true

      this.startCountdownTimer(UPDATE_COUNTDOWN_SECONDS)
      this.saveToStorage()

      // Notify other tabs about the update
      if (this.swUpdateChannel) {
        try {
          this.swUpdateChannel.postMessage({
            type: 'SW_UPDATE_AVAILABLE',
            countdown: UPDATE_COUNTDOWN_SECONDS,
          })
        } catch (error) {
          // Silently handle broadcast errors
        }
      }
    },

    formatCountdown(seconds) {
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    },
  },
}
